import React, { useState, useEffect } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import ExitDetailsForm from './ExitDetailsForm';
import ReviewShareholdings from './ReviewShareholdings';
import ReviewShareConsiderations from './ReviewShareConsiderations';
import ButtonsAndErrorModals from './ButtonsAndErrorModals';
import ConsiderationHistory from './ExitDetailsForm/ConsiderationHistory';
import FullPageSuccess from 'components/FullPageSuccess';
import {
  EXITS,
  EXITS_EDIT,
  createExitStatementAddRoute,
} from 'adminConstants/routes';
import downloadFile from 'utils/downloadFile';
import base64ToBlob from 'utils/base64ToBlob';
import { BottomTextContent } from './BottomTextContent';
import { format } from 'date-fns';
import PageContent from 'components/PageContent';
import RecordView from 'components/RecordView';
import Heading from 'components/Heading';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  ConsiderationType,
  ShareConsiderationAllocationMethod,
} from 'further-types/exit';
import { styled } from '@mui/material/styles';
import type { ExitDetailsFormValues } from './ExitDetailsForm';
import type { ShareConsideration } from './types';

const StyledHeading = styled(Heading)(() => ({
  marginTop: '2rem',
}));

type Shareholding = {
  shareholdingId: string;
  sharesAllocatedForSale: number;
  totalSaleValue: number;
  taxableValue: number;
  performanceFee: number;
  sharePrice: number;
  taxableValueSharePrice: number;
  accruedFeesCharged?: number;
  additionalGrossReceivedValue?: number;
  grossTaxableValue?: number;
};

const CreateExit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isBeingEdited = useRouteMatch(EXITS_EDIT)?.isExact;

  const [previousExitDetails, setPreviousExitDetails] = useState<
    ExitDetailsFormValues | undefined
  >(undefined);
  const [exitDetails, setExitDetails] = useState<
    ExitDetailsFormValues | undefined
  >(undefined);
  const [exitDetailsStepCompleted, setExitDetailsStepCompleted] =
    useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shareholdings, setShareholdings] = useState<Array<Shareholding>>([]);
  const [shareConsiderations, setShareConsiderations] = useState<
    Array<ShareConsideration>
  >([]);
  const [exitId, setExitId] = useState(id);
  const [amendmentId, setAmendmentId] = useState<string | undefined>(undefined);
  const [disableTable, setDisableTable] = useState<boolean>(false);
  const [isUploadComplete, setIsUploadComplete] = useState<boolean>(false);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleMutation = useHandleTrpcMutation();
  const exit = api.exit.getById.useQuery(
    { id: id! },
    {
      enabled: !!id,
    },
  );

  const create = api.exit.create.useMutation(
    handleMutation({ refetchType: 'none' }),
  );

  const createShareConsiderationExit =
    api.exit.createShareConsiderationExit.useMutation(
      handleMutation({ refetchType: 'none' }),
    );

  const edit = api.exit.edit.useMutation(
    handleMutation({ refetchType: 'none' }),
  );

  const editShareConsiderationExit =
    api.exit.editShareConsiderationExit.useMutation(
      handleMutation({ refetchType: 'none' }),
    );

  const considerationHistoryEnabled =
    !!exitId &&
    !!exit.data?.firmId &&
    exitDetailsStepCompleted &&
    isBeingEdited;

  const considerationHistory = api.exit.getConsiderationHistory.useQuery(
    {
      exitId: exitId ?? '',
      firmId: exit.data?.firmId ?? '',
    },
    {
      enabled: considerationHistoryEnabled,
    },
  );

  const downloadExit = api.exit.download.useMutation(
    useHandleTrpcMutation<ApiResponses['exit']['download']>()({
      onSuccess: (data) => {
        const blob = base64ToBlob(data.file, data.mimeType);
        downloadFile(blob, data.filename || `Exit-${Date.now()}.xlsx`);
      },
    }),
  );

  const handleDownload = async () => {
    if (!exitId) return;
    setIsDownloading(true);
    downloadExit.mutate(
      { id: exitId },
      {
        onSettled: () => {
          setIsDownloading(false);
        },
      },
    );
  };

  useEffect(() => {
    if (isBeingEdited && id && exit.data) {
      const exitData = exit.data as typeof exit.data & {
        shareConsiderationExit?: boolean;
      };
      const {
        firmId,
        firmName,
        date,
        companyIds,
        companies,
        noSharesExited,
        sharePrice,
        taxReliefElegible,
        taxReliefElegibleAllocationIds,
        taxableValueSharePrice,
        shareConsiderationExit,
      } = exitData || {};

      const details: ExitDetailsFormValues = {
        firmId: firmId ?? '',
        exitDate: new Date(date),
        firmName: firmName ?? '',
        selectedCompany: {
          _id: companies?.[0]?._id ?? '',
          tradingName: companies?.[0]?.tradingName ?? '',
        },
        sharesToBeSold: Number(noSharesExited),
        sharePrice: sharePrice?.toString() ?? '0',
        taxableValueSharePrice: taxableValueSharePrice?.toString() ?? '0',
        isReceivedValueSharePriceEdited: false,
        chargeAccruedFees: true,
        taxReliefElegible: taxReliefElegible ?? undefined,
        selectedShareClasses: companyIds || [],
        selectedAllocations: exit.data?.shareAllocationUploadIds || [],
        taxReliefElegibleAllocationIds: taxReliefElegibleAllocationIds || [],
        considerationType: shareConsiderationExit
          ? ConsiderationType.Share
          : ConsiderationType.Cash,
        shareConsiderationCompanyId: undefined,
        shareConsiderationShareClassId: undefined,
        totalConsiderationShares: '0',
        shareConsiderationValuePerShare: '0',
        shareConsiderationValueTotal: '0',
        shareConsiderationEisEligible: undefined,
        shareConsiderationAllocationMethod: undefined,
        permitPartialShareAllocation: false,
      };
      setExitDetails(details);
      setPreviousExitDetails(details);
    }
  }, [id, isBeingEdited, exit.data]);

  useEffect(() => {
    if (
      create.data ||
      createShareConsiderationExit.data ||
      edit.data ||
      editShareConsiderationExit.data
    ) {
      setAmendmentId(
        edit?.data?.exitAmendmentId ??
          editShareConsiderationExit?.data?.exitAmendmentId,
      );
      setIsSuccess(true);
    }

    if (create.data?._id) {
      setExitId(create.data._id);
    }

    if (createShareConsiderationExit.data?._id) {
      setExitId(createShareConsiderationExit.data._id);
    }
  }, [
    create.data,
    createShareConsiderationExit.data,
    edit.data,
    editShareConsiderationExit.data,
  ]);

  if (isSuccess) {
    return (
      <FullPageSuccess
        title="Your exit has been successfully processed"
        additionalText="Your investors will now be able to view this exit in their investor portals. Would you like to send an exit statement?"
        proceedText="Send exit statement"
        proceedLink={createExitStatementAddRoute(
          exitId ?? '',
          amendmentId ?? '',
        )}
        secondaryText="Do this later"
        secondaryLink={EXITS}
        bottomText={
          <BottomTextContent
            disableButton={isDownloading}
            handleDownload={handleDownload}
          />
        }
      />
    );
  }

  const removeEmptyShareholdingRows = (sh) => {
    if (
      !sh.sharesAllocatedForSale &&
      !sh.accruedFeesCharged &&
      !sh.performanceFee &&
      !sh.totalSaleValue
    )
      return false;
    return true;
  };

  const onSubmit = async () => {
    if (!exitDetails) return;
    if (!exitDetails.taxReliefElegible) return;

    const commonMutationInput = {
      firmId: exitDetails.firmId ?? '',
      sharePrice: Number(exitDetails.sharePrice),
      taxableValueSharePrice: Number(exitDetails.taxableValueSharePrice),
      taxReliefElegible: exitDetails.taxReliefElegible ?? undefined,
      taxReliefElegibleAllocationIds:
        exitDetails.taxReliefElegibleAllocationIds,
    };

    if (isBeingEdited) {
      if (!exitId) return;

      // Check if this is a share consideration exit
      const isShareConsideration =
        exitDetails.considerationType === ConsiderationType.Share;

      if (isShareConsideration) {
        editShareConsiderationExit.mutate({
          ...commonMutationInput,
          shareConsiderationsToBeExited: shareConsiderations
            .filter(
              (sc) =>
                sc.sharesAllocatedForSale > 0 ||
                (sc.newSharesAllocated ?? 0) > 0,
            )
            .map((sc) => ({
              shareholdingId: sc.shareholdingId,
              sharesAllocatedForSale: sc.sharesAllocatedForSale,
              newSharesAllocated: sc.newSharesAllocated ?? 0,
            })),
          exitAmendmentDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          exitId,
          shareConsiderationValuePerShare: Number(
            exitDetails.shareConsiderationValuePerShare ?? '0',
          ),
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId ?? '',
        });
      } else {
        edit.mutate({
          ...commonMutationInput,
          shareholdingsToBeExited: shareholdings
            .filter(removeEmptyShareholdingRows)
            .map((sh) => ({
              shareholdingId: sh.shareholdingId,
              sharesAllocatedForSale: sh.sharesAllocatedForSale,
              totalSaleValue: sh.totalSaleValue,
              taxableValue: sh.grossTaxableValue ?? sh.taxableValue,
              performanceFee: sh.performanceFee,
              sharePrice: sh.sharePrice,
              additionalGrossReceivedValue:
                sh.additionalGrossReceivedValue ?? 0,
              ...(sh.accruedFeesCharged !== undefined
                ? { accruedFeesCharged: sh.accruedFeesCharged }
                : {}),
            })),
          exitAmendmentDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          exitId,
        });
      }
    } else {
      // Check if this is a share consideration exit
      const isShareConsideration =
        exitDetails.considerationType === ConsiderationType.Share;

      if (isShareConsideration) {
        createShareConsiderationExit.mutate({
          ...commonMutationInput,
          shareAllocationUploadIds: exitDetails.selectedAllocations,
          shareConsiderationsToBeExited: shareConsiderations
            .filter(
              (sc) =>
                sc.sharesAllocatedForSale > 0 ||
                (sc.newSharesAllocated ?? 0) > 0,
            )
            .map((sc) => ({
              shareholdingId: sc.shareholdingId,
              sharesAllocatedForSale: sc.sharesAllocatedForSale,
              newSharesAllocated: sc.newSharesAllocated ?? 0,
            })),
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          companyIds: exitDetails.selectedShareClasses,
          shareConsiderationCompanyId:
            exitDetails.shareConsiderationCompanyId ?? '',
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId ?? '',
          shareConsiderationValuePerShare: Number(
            exitDetails.shareConsiderationValuePerShare ?? '0',
          ),
          totalConsiderationShares: Number(
            exitDetails.totalConsiderationShares ?? '0',
          ),
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible ?? false,
        });
      } else {
        create.mutate({
          ...commonMutationInput,
          shareAllocationUploadIds: exitDetails.selectedAllocations,
          shareholdingsToBeExited: shareholdings
            .filter(removeEmptyShareholdingRows)
            .map((sh) => ({
              shareholdingId: sh.shareholdingId,
              sharesAllocatedForSale: sh.sharesAllocatedForSale,
              totalSaleValue: sh.totalSaleValue,
              taxableValue: sh.grossTaxableValue ?? sh.taxableValue,
              performanceFee: sh.performanceFee,
              sharePrice: sh.sharePrice,
              ...(sh.accruedFeesCharged !== undefined
                ? { accruedFeesCharged: sh.accruedFeesCharged }
                : {}),
            })),
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          companyIds: exitDetails.selectedShareClasses,
        });
      }
    }
  };

  const onCancel = () => {
    setExitDetailsStepCompleted(false);
    // Only reset exitDetails in edit mode - for new exits, keep the form values
    if (isBeingEdited) {
      setExitDetails(previousExitDetails);
    }
    // Clear only table-related state
    setShareholdings([]);
    setShareConsiderations([]);
    setDisableTable(false);
    setIsUploadComplete(false);
  };

  const pageTitle = isBeingEdited
    ? 'Exits: Update exit'
    : 'Exits: Process new exit';

  // Hide buttons when upload section is visible (before upload is complete)
  const shouldHideButtons =
    exitDetails?.considerationType === ConsiderationType.Share &&
    exitDetails?.shareConsiderationAllocationMethod ===
      ShareConsiderationAllocationMethod.Upload &&
    !isBeingEdited &&
    shareConsiderations.length > 0 &&
    !isUploadComplete;

  return (
    <PageContainer heading={pageTitle}>
      <PageContent header="Enter exit details">
        <RecordView size="full">
          <ExitDetailsForm
            isBeingEdited={isBeingEdited}
            previousExitDetails={
              isBeingEdited
                ? previousExitDetails
                : exitDetails
                ? {
                    firmId: exitDetails.firmId,
                    firmName: exitDetails.firmName,
                    exitDate: exitDetails.exitDate,
                    selectedCompany: exitDetails.selectedCompany,
                    selectedShareClasses: exitDetails.selectedShareClasses,
                    sharesToBeSold: exitDetails.sharesToBeSold,
                    sharePrice: exitDetails.sharePrice,
                    taxableValueSharePrice: exitDetails.taxableValueSharePrice,
                    chargeAccruedFees: exitDetails.chargeAccruedFees,
                    taxReliefElegible: exitDetails.taxReliefElegible,
                    taxReliefElegibleAllocationIds:
                      exitDetails.taxReliefElegibleAllocationIds,
                    shareAllocationUploadIds: exitDetails.selectedAllocations,
                    considerationType: exitDetails.considerationType,
                    shareConsiderationCompanyId:
                      exitDetails.shareConsiderationCompanyId,
                    shareConsiderationShareClassIds:
                      exitDetails.shareConsiderationShareClassId
                        ? [exitDetails.shareConsiderationShareClassId]
                        : undefined,
                    totalConsiderationShares:
                      exitDetails.totalConsiderationShares,
                    shareConsiderationValuePerShare:
                      exitDetails.shareConsiderationValuePerShare,
                    shareConsiderationValueTotal:
                      exitDetails.shareConsiderationValueTotal,
                    shareConsiderationEisEligible:
                      exitDetails.shareConsiderationEisEligible,
                    shareConsiderationAllocationMethod:
                      exitDetails.shareConsiderationAllocationMethod,
                    permitPartialShareAllocation:
                      exitDetails.permitPartialShareAllocation,
                  }
                : undefined
            }
            onSubmit={(details) => {
              const formValues: ExitDetailsFormValues = {
                firmId: details.firmId ?? '',
                firmName: details.firmName ?? '',
                exitDate: details.exitDate,
                selectedCompany: {
                  _id: details.selectedCompany?._id ?? '',
                  tradingName: details.selectedCompany?.tradingName ?? '',
                },
                sharesToBeSold: details.sharesToBeSold ?? 0,
                sharePrice: details.sharePrice.toString(),
                taxableValueSharePrice:
                  details.taxableValueSharePrice.toString(),
                isReceivedValueSharePriceEdited: false,
                chargeAccruedFees: details.chargeAccruedFees,
                taxReliefElegible: details.taxReliefElegible,
                selectedShareClasses: details.selectedShareClasses ?? [],
                selectedAllocations: details.selectedAllocations ?? [],
                taxReliefElegibleAllocationIds:
                  details.taxReliefElegibleAllocationIds ?? [],
                considerationType: details.considerationType,
                shareConsiderationCompanyId:
                  details.shareConsiderationCompanyId,
                shareConsiderationShareClassId:
                  details.shareConsiderationShareClassId,
                totalConsiderationShares:
                  details.totalConsiderationShares?.toString() ?? '0',
                shareConsiderationValuePerShare:
                  details.shareConsiderationValuePerShare?.toString() ?? '0',
                shareConsiderationValueTotal:
                  details.shareConsiderationValueTotal?.toString() ?? '0',
                shareConsiderationEisEligible:
                  details.shareConsiderationEisEligible,
                shareConsiderationAllocationMethod:
                  details.shareConsiderationAllocationMethod,
                permitPartialShareAllocation:
                  details.permitPartialShareAllocation ?? false,
              };
              setExitDetails(formValues);
              setExitDetailsStepCompleted(true);
              setIsUploadComplete(false);
            }}
            exitDetailsStepCompleted={exitDetailsStepCompleted}
          />
        </RecordView>

        {considerationHistoryEnabled &&
          exitId &&
          exit.data?.firmId &&
          considerationHistory.data &&
          considerationHistory.data.length > 0 && (
            <>
              <ConsiderationHistory exitId={exitId} firmId={exit.data.firmId} />
            </>
          )}

        {exitDetailsStepCompleted && (
          <>
            <StyledHeading variant="h2">
              Review and update exit results
            </StyledHeading>

            {exitDetails &&
            exitDetails.considerationType === ConsiderationType.Share ? (
              <ReviewShareConsiderations
                exitDetails={exitDetails}
                shareConsiderations={shareConsiderations}
                setShareConsiderations={setShareConsiderations}
                disableTable={disableTable ?? false}
                isBeingEdited={isBeingEdited ?? false}
                exitId={exitId}
                onUploadComplete={() => setIsUploadComplete(true)}
              />
            ) : exitDetails ? (
              <ReviewShareholdings
                exitDetails={exitDetails}
                previousExitDetails={previousExitDetails}
                shareholdings={shareholdings}
                setShareholdings={setShareholdings}
                disableTable={disableTable}
                isBeingEdited={isBeingEdited}
                exitId={exitId}
              />
            ) : null}
            {shouldHideButtons ? null : (
              <ButtonsAndErrorModals
                exitDetails={exitDetails}
                shareholdings={shareholdings}
                setShareholdings={setShareholdings}
                shareConsiderations={shareConsiderations}
                setShareConsiderations={setShareConsiderations}
                onSubmit={onSubmit}
                onCancel={onCancel}
                isLoading={
                  edit.isPending ||
                  create.isPending ||
                  createShareConsiderationExit.isPending ||
                  editShareConsiderationExit.isPending
                }
                disableTable={disableTable}
                isBeingEdited={isBeingEdited ?? false}
                previousExitDetails={previousExitDetails}
              />
            )}
          </>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default CreateExit;
